import { Badge, Button, Form } from 'react-bootstrap';
import type { RecommendationLabInputOptions } from '../../models/RecommendationLab';
import {
  availableSurveyOptionValues,
  normalizeSurveyAnswers,
  resolveSurveyOptions,
  toggleSurveyAnswer,
  type RecommendationLabSurveyAnswers,
} from './surveyAnswers';

interface RecommendationSurveyFormProps {
  inputOptions: RecommendationLabInputOptions;
  answers: RecommendationLabSurveyAnswers;
  onChange: (answers: RecommendationLabSurveyAnswers) => void;
}

export function RecommendationSurveyForm({ inputOptions, answers, onChange }: RecommendationSurveyFormProps) {
  return (
    <>
      {inputOptions.survey.questions.map((question) => {
        const selected = answers[question.questionId] ?? [];
        const optionCatalog = resolveSurveyOptions(inputOptions, question);
        const availableValues = availableSurveyOptionValues(inputOptions, question, answers);
        const availableOptions = optionCatalog.filter((option) => availableValues.has(option.value));
        const updateAnswer = (values: string[]) =>
          onChange(
            normalizeSurveyAnswers(inputOptions, {
              ...answers,
              [question.questionId]: values,
            }),
          );
        return (
          <Form.Group key={question.questionId} className="mb-3">
            <Form.Label className="small mb-1 fw-semibold">
              {question.title} {question.required && <span className="text-danger">*</span>}
            </Form.Label>
            {question.description && <div className="small text-muted mb-1">{question.description}</div>}
            {question.type === 'single' ? (
              <Form.Select
                aria-label={question.title}
                size="sm"
                value={selected[0] ?? ''}
                onChange={(event) => updateAnswer(event.target.value ? [event.target.value] : [])}
              >
                <option value="">— Select —</option>
                {availableOptions.map((option) => (
                  <option key={option.optionId} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {availableOptions.map((option) => {
                  const selectedIndex = selected.indexOf(option.value);
                  return (
                    <Button
                      key={option.optionId}
                      size="sm"
                      variant={selectedIndex >= 0 ? 'primary' : 'outline-secondary'}
                      aria-pressed={selectedIndex >= 0}
                      onClick={() =>
                        updateAnswer(
                          toggleSurveyAnswer({ ...question, options: optionCatalog }, selected, option.value),
                        )
                      }
                    >
                      {question.type === 'ranked_multi' && selectedIndex >= 0 && (
                        <Badge bg="light" text="dark" className="me-1">
                          {selectedIndex + 1}
                        </Badge>
                      )}
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </Form.Group>
        );
      })}
    </>
  );
}
